import React from 'react'
import { Helmet } from 'react-helmet'
import useLocation from 'react-use/lib/useLocation'
import { helmetJsonLdProp } from 'react-schemaorg'
import { Organization, WebSite } from 'schema-dts'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { Post, PostResumeSample } from '../../types/post'
import { LandingPage, PostLink } from '../../types/landingPage'
import { CategoryWithCTA } from '../../types/category'
import PostSEO from './PostSEO'
import CategorySEO from './CategorySEO'
import LandingPageSEO from './LandingPageSEO'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import useBrand from '../../hooks/useBrand'
import { LandingPageV2 } from '../../types/landingPageV2'
import { FAQPage } from '../../types/faq'
import { ReviewPage } from '../../types/review'
import { ProductPage } from '../../types/productPageV2'
import useTrustPilot from '../../hooks/useTrustPilot'
import getAddress from '../../utils/getAddress'
import { ModifiedPressArticlePage } from 'types/press'
import PressArticleSEO from './PressArticleSEO'
import useSiteMetadata from '../../hooks/useSiteMetadata'

export type SEOProps =
  | {
      noIndex?: boolean
    }
  | ({
      noIndex?: boolean
    } & (
      | {
          post: Post | PostResumeSample
        }
      | {
          landingPage:
            | LandingPage
            | LandingPageV2
            | FAQPage
            | ReviewPage
            | ProductPage
        }
      | {
          category: CategoryWithCTA
          categoryPosts: PostLink[]
        }
      | {
          pressArticle: ModifiedPressArticlePage
        }
    ))

const SEO: React.FC<SEOProps> = (props) => {
  const brand = useBrand()
  const { urlPathBuilder, baseURL, blogPrefix } = useUrlPathBuilder()
  const {
    i18n: { language },
  } = useTranslation()
  const trustPilot = useTrustPilot()
  const address = getAddress(brand.default_company_address)
  const { siteUrl } = useSiteMetadata()

  return (
    <>
      <Helmet
        script={[
          helmetJsonLdProp<Organization>({
            ...(!brand.isZipJob ? { alternateName: brand.alternateName } : {}),
            '@context': 'https://schema.org',
            '@type': 'Organization',
            '@id': urlPathBuilder([], {
              baseURL,
              hash: 'organization',
            }),
            name: brand.name,
            url: brand.productionUrl,
            logo: `https:${brand.logo?.file.url}`,
            telephone: brand.phoneNumber,
            parentOrganization: brand.parentOrganization,
            sameAs: [
              brand.twitter && `https://twitter.com/${brand.twitter}`,
              brand.linkedIn,
              brand.youTube,
              brand.facebook,
              brand.instagram,
            ].filter((url): url is string => Boolean(url)),
            aggregateRating: {
              '@type': 'AggregateRating',
              bestRating: '5',
              ratingValue: trustPilot?.businessUnit.trustScore,
              reviewCount: trustPilot?.businessUnit.numberOfReviews.total,
              url: trustPilot?.links?.profileUrl,
              worstRating: '1',
            },
            address: {
              '@type': 'PostalAddress',
              '@id': urlPathBuilder([], {
                baseURL,
                hash: 'address',
              }),
              streetAddress: address.streetAddress,
              addressLocality: address.addressLocality,
              addressRegion: address.addressRegion,
              postalCode: address.postalCode,
            },
          }),
          helmetJsonLdProp<WebSite>({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            '@id': urlPathBuilder([], {
              baseURL,
              hash: 'website',
            }),
            url: urlPathBuilder([], {
              baseURL,
            }),
            name: brand.name,
            description: brand.name,
            publisher: {
              '@id': urlPathBuilder([], {
                baseURL,
                hash: 'organization',
              }),
            },
            potentialAction: {
              '@type': 'SearchAction',
              target:
                urlPathBuilder([blogPrefix, 'search'], {
                  baseURL,
                }) + `?s={search_term_string}`,
              // @ts-ignore
              'query-input': 'required name=search_term_string',
            },
            inLanguage: language,
          }),
        ]}
      >
        <meta property="og:type" content="website" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        {!brand.isZipJob && brand.name && (
          <link
            rel="apple-touch-icon"
            href={`${siteUrl}/images/universal/meta/logo-${brand.name.toLowerCase()}-square_inverse.png`}
          ></link>
        )}
        {brand && brand?.favicon?.file?.url && (
          <link rel="shortcut icon" href={brand.favicon.file.url} />
        )}
        {brand && brand?.rssFeedTitle && brand?.rssFeedHref && (
          <link
            rel="alternate"
            type="application/rss+xml"
            title={brand.rssFeedTitle}
            href={brand.rssFeedHref}
          />
        )}
      </Helmet>
      {'post' in props && props.post && (
        <PostSEO post={props.post} shouldRenderNoIndex={!props.noIndex} />
      )}
      {'category' in props && props.category && (
        <CategorySEO
          category={props.category}
          categoryPosts={props.categoryPosts}
          shouldRenderNoIndex={!props.noIndex}
        />
      )}
      {'landingPage' in props && props.landingPage && (
        <LandingPageSEO
          landingPage={props.landingPage}
          shouldRenderNoIndex={!props.noIndex}
        />
      )}
      {'pressArticle' in props && props.pressArticle && (
        <PressArticleSEO
          pressArticle={props.pressArticle}
          shouldRenderNoIndex={!props.noIndex}
        />
      )}
      {props.noIndex && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
    </>
  )
}

export default SEO
